import React, { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { Inline } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

type HiringManagerToolPayload = ActionRendererKeyToPayload[typeof ActionKeys.HiringManagerTool];
type HiringManagerChoice = HiringManagerToolPayload['choices'][number];

// Primary button keys that should appear as blue (primary) buttons
const PRIMARY_BUTTON_KEYS = ['confirm', 'yes', 'approve'];

const renderButtons = (
	choices: HiringManagerToolPayload['choices'],
	onButtonClick: (choice: HiringManagerChoice) => void,
	isDisabled: boolean,
	isLoading: boolean,
	invocationId: string,
) => {
	return choices.map((choice) => {
		const isPrimary = PRIMARY_BUTTON_KEYS.includes(choice.key.toLowerCase());
		return (
			<Button
				key={`${invocationId}-${choice.key}`}
				appearance={isPrimary ? 'primary' : 'default'}
				onClick={() => onButtonClick(choice)}
				isDisabled={isDisabled}
				isLoading={isLoading}
			>
				{choice.title}
			</Button>
		);
	});
};

export const HiringManagerToolUIAction = ({
	actions,
	getStatus,
}: ActionRendererFor<typeof ActionKeys.HiringManagerTool>): React.JSX.Element | null => {
	di(usePublish);

	const publish = usePublish('ai-mate');
	const firstAction = actions[0] as ActionWith<HiringManagerToolPayload>;

	const status = getStatus(firstAction);
	const isDisabled = status !== 'idle';
	const isLoading = status === 'started';

	const invocationId = firstAction.invocationId ?? firstAction.actionId ?? 'hiring-manager-tool';

	const handleButtonClick = useCallback(
		(choice: HiringManagerChoice) => {
			// Use 'insert-prompt' to ensure agentId is included for tool calls
			publish({
				type: 'insert-prompt',
				source: 'ai-mate',
				data: {
					prompt: choice.title,
				},
			});
		},
		[publish],
	);

	const choices = firstAction.data?.choices ?? [];

	if (choices.length === 0) {
		return null;
	}

	return (
		<Inline space="space.150">
			{renderButtons(choices, handleButtonClick, isDisabled, isLoading, invocationId)}
		</Inline>
	);
};
